<script setup>
import { useModal } from 'vue-final-modal';

import routes from '@/constants/routes';
import analyticEventKeys from '@/constants/analytic-event-keys';

defineProps({
  isFormButtonVisible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['searchClick']);

const { t } = useI18n();
const analytics = useGoogleAnalytics();

// modals
const { authEmailModal } = useAuthModals();

const settingsModal = useModal({
  component: resolveComponent('ModalsSettings'),
});

// auth
const authStore = useAuthStore();

function handleAuthClick() {
  if (authStore.isGuest) {
    authEmailModal.open();
  } else {
    settingsModal.open();
  }
}

// redirect
function handleRedirect(link) {
  analytics.pushEvent({
    event: analyticEventKeys.MENU_NAVIGATE,
    label: link,
  });
}

defineExpose({ handleAuthClick });
</script>

<template>
  <div class="bundles-account-navigation-buttons">
    <!--   Form     -->
    <UiButton
      v-if="isFormButtonVisible"
      class="bundles-account-navigation-buttons__button hidden-desktop"
      aria-label="Search form"
      theme="transparent"
      @click="emit('searchClick')"
    >
      <CIcon
        class="bundles-account-navigation-buttons__icon bundles-account-navigation-buttons__icon--search"
        name="search-2"
      />
    </UiButton>

    <!--   My tickets     -->
    <CLocalizedLink
      v-if="authStore.account?.activeOrderTicketsCount || !authStore.isGuest"
      class="bundles-account-navigation-buttons__button"
      :to="routes.MY_TICKETS"
      aria-label="My tickets"
    >
      <LabelsStatusCircle
        :has-status="!!authStore.account?.activeOrderTicketsCount"
        :count="authStore.account?.activeOrderTicketsCount"
      >
        <CIcon
          class="bundles-account-navigation-buttons__icon bundles-account-navigation-buttons__icon--link"
          name="ticket"
        />
      </LabelsStatusCircle>
    </CLocalizedLink>

    <!--   Cart     -->
    <CLocalizedLink
      v-if="authStore.account?.activeCartTicketsCount"
      class="bundles-account-navigation-buttons__button"
      :to="routes.CART"
      aria-label="Cart"
      @click="handleRedirect(routes.CART)"
    >
      <LabelsStatusCircle
        :has-status="!!authStore.account?.activeCartTicketsCount"
        :count="authStore.account?.activeCartTicketsCount"
      >
        <CIcon
          class="bundles-account-navigation-buttons__icon bundles-account-navigation-buttons__icon--link"
          name="cart"
        />
      </LabelsStatusCircle>
    </CLocalizedLink>

    <!--  Account    -->
    <UiButton
      class="bundles-account-navigation-buttons__button bundles-account-navigation-buttons__button--account"
      theme="outlined-transparent"
      aria-label="My account"
      @click="handleAuthClick"
    >
      <div class="bundles-account-navigation-buttons__wrapper">
        <CIcon
          class="bundles-account-navigation-buttons__icon bundles-account-navigation-buttons__icon--user"
          name="user-3"
        />

        <span class="bundles-account-navigation-buttons__font">
          {{ t('form.myAccount') }}
        </span>
      </div>
    </UiButton>
  </div>
</template>
